import type { JSX, MouseEvent, ReactNode, TouchEvent } from 'react';
import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { generateRowsChunks } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import type { ArrowControlProps } from './components';
import { ArrowControl } from './components';
import { SnapCarouselItem } from './snap-carousel-item';
import type {
  ColumnChunkNode,
  SnapCarouselAPI,
  SnapCarouselProps
} from './types';
import { updateRowHeight, useSlideScrollAnimation } from './utils';

const SCROLL_END_SENSITIVITY = 40;
const SLIDE_ANIMATION_DURATION = 300;

export const SnapCarousel = forwardRef<SnapCarouselAPI, SnapCarouselProps>(
  (
    {
      className,
      rows = 1,
      columns = 1,
      rowsGap = 6,
      columnsGap = 8,
      alignColumnCenter,
      boundariesPadding = 0,
      arrowControl: ArrowComponent = ArrowControl,
      initialSnappedElement = 0,
      scrollEndSensitivity = SCROLL_END_SENSITIVITY,
      curveOfSlideTransition = 'easeOut',
      durationTransition = SLIDE_ANIMATION_DURATION,
      onSlideChange,
      onUpdateScrollState,
      withArrows = true,
      withDrag = false,
      children
    },
    forwardingRef
  ) => {
    const [snappedColumnIndex, setSnappedColumnIndex] = useState(
      initialSnappedElement
    );
    const [[isScrollStart, isScrollEnd], setScrollState] = useState([
      !initialSnappedElement,
      false
    ]);
    const [isDraggingState, setIsDraggingState] = useState(false);

    const carouselRef = useRef<HTMLDivElement | null>(null);
    // used to optimize swiping using native scrolling
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    // used to fast scroll feature.
    // remembers quick presses and resets to `snappedColumnIndex` then scroll ends
    const scrollIndexRef = useRef(0);

    const isDragging = useRef(false);
    const startX = useRef(0);
    const startScrollLeft = useRef(0);

    const validItems = Children.toArray(children).filter(isValidElement);
    const itemsPerSlide = columns * rows;

    const totalItems = validItems.length;
    const totalSlides = Math.ceil(totalItems / columns / rows);

    const dataRowsChunks: ReactNode[] = generateRowsChunks(
      validItems,
      columns,
      rows,
      rowsGap
    );

    const generatedItems = generateData({
      items: dataRowsChunks,
      renderItem: (item, i) => (
        <SnapCarouselItem
          key={i}
          snapAlignCenter={alignColumnCenter}
          boundariesPadding={boundariesPadding}
        >
          {item}
        </SnapCarouselItem>
      )
    });

    const scrollToAnimation = useSlideScrollAnimation({
      carouselRef,
      curveOfSlideTransition,
      columnsGap,
      durationTransition,
      boundariesPadding
    });

    const slideToIndex = (itemIndex: number, instantly = false) => {
      if (itemIndex >= 0) {
        const columnIndex = dataRowsChunks.findIndex((chunk) => {
          const column = chunk as ColumnChunkNode | ColumnChunkNode[];

          if (Array.isArray(column)) {
            return !!column.find((row) => row.index === itemIndex);
          }

          return column.index === itemIndex;
        });

        const startColumnSlideIndex =
          Math.floor(columnIndex / columns) * columns;

        scrollToAnimation(startColumnSlideIndex, instantly);
      }
    };

    const slideToColumn = (itemIndex: number, instantly = false) => {
      const columnIndex = Math.floor(itemIndex / rows);

      scrollToAnimation(columnIndex, instantly);
    };

    const updateScrollState = useCallback(() => {
      clearTimeout(scrollTimeoutRef.current);
      const { current } = carouselRef;

      if (!current) {
        return;
      }

      const scrollWidth = current.scrollLeft + current.offsetWidth;
      const { left: parentLeft } = current.getBoundingClientRect();

      // Renew timer
      scrollTimeoutRef.current = setTimeout(() => {
        let closestIndex = 0;
        let minDistance = Infinity;

        // finding the nearest snapped index
        current.childNodes.forEach((element, index) => {
          const rect = (element as Element).getBoundingClientRect();

          const distance = Math.abs(
            rect.left - parentLeft - boundariesPadding / 2
          );

          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });

        setSnappedColumnIndex(closestIndex);
        scrollIndexRef.current = closestIndex * rows;
      }, 100);

      const isScrollStart = current.scrollLeft <= 0;
      const isScrollEnd =
        scrollWidth + Math.min(scrollEndSensitivity, boundariesPadding) >=
        current.scrollWidth - 1;

      onUpdateScrollState?.(isScrollStart, isScrollEnd);
      setScrollState([isScrollStart, isScrollEnd]);
    }, [boundariesPadding, rows, onUpdateScrollState, scrollEndSensitivity]);

    const handlePrevClick = () => {
      scrollIndexRef.current = scrollIndexRef.current - itemsPerSlide;

      if (scrollIndexRef.current >= 0) {
        slideToColumn(scrollIndexRef.current);
      } else {
        slideToColumn(0);
      }
    };

    const handleNextClick = () => {
      scrollIndexRef.current = scrollIndexRef.current + itemsPerSlide;

      if (scrollIndexRef.current < totalItems) {
        slideToColumn(scrollIndexRef.current);
      } else {
        slideToColumn(totalItems);
      }
    };

    const startScrolling = useCallback(
      (event: TouchEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>) => {
        if (!carouselRef.current) {
          return;
        }

        if (event.type === 'touchstart') {
          event.preventDefault();
        }

        isDragging.current = true;
        setIsDraggingState(true);
        startX.current =
          event.type === 'touchstart'
            ? (event as TouchEvent).touches[0].pageX
            : (event as MouseEvent).pageX;
        startScrollLeft.current = carouselRef.current.scrollLeft;

        carouselRef.current.style.scrollSnapType = 'none';
      },
      []
    );

    const endScrolling = useCallback(() => {
      requestAnimationFrame(() => {
        if (!carouselRef.current) {
          return;
        }

        isDragging.current = false;
        setIsDraggingState(false);

        const currentScroll = carouselRef.current.scrollLeft;
        carouselRef.current.style.scrollSnapType = '';

        carouselRef.current.scrollTo({
          left: currentScroll,
          behavior: 'smooth'
        });
      });
    }, []);

    const scrollWithDrag = useCallback(
      (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
        if (!isDragging.current || !carouselRef.current) {
          return;
        }

        if (e.type === 'touchmove') {
          e.preventDefault();
        }

        const x =
          e.type === 'touchmove'
            ? (e as TouchEvent).touches[0].pageX
            : (e as MouseEvent).pageX;
        const deltaX = x - startX.current;

        carouselRef.current.scrollLeft = startScrollLeft.current - deltaX;
      },
      []
    );

    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    useEffect(() => {
      if (!carouselRef.current) {
        return;
      }

      resizeObserverRef.current = new ResizeObserver(updateScrollState);
      resizeObserverRef.current.observe(carouselRef.current);

      return () => {
        resizeObserverRef.current?.disconnect();
      };
    }, [updateScrollState]);

    const carouselAPI: SnapCarouselAPI = {
      ref: carouselRef,
      snappedColumnIndex,
      totalSlides,
      isScrollStart,
      isScrollEnd,
      slideToIndex,
      slideToColumn,
      slideNext: handleNextClick,
      slidePrev: handlePrevClick
    };

    useImperativeHandle(forwardingRef, () => carouselAPI, [carouselAPI]);

    useEffect(() => {
      if (onSlideChange) {
        onSlideChange(snappedColumnIndex);
      }
    }, [snappedColumnIndex]);

    // refresh carousel to calculate scroll state
    useEffect(() => {
      const request = requestAnimationFrame(() => {
        updateRowHeight(carouselRef, rows);
        updateScrollState();
      });

      return () => cancelAnimationFrame(request);
    }, [children]);

    const arrowControlPropsLeft: ArrowControlProps = {
      direction: 'left',
      onClick: handlePrevClick
    };

    const arrowControlPropsRight: ArrowControlProps = {
      direction: 'right',
      onClick: handleNextClick
    };

    const dragHandlers = withDrag
      ? {
          onMouseDown: startScrolling,
          onMouseMove: scrollWithDrag,
          onMouseUp: endScrolling,
          onMouseLeave: endScrolling,
          onTouchStart: startScrolling,
          onTouchMove: scrollWithDrag,
          onTouchEnd: endScrolling,
          onTouchCancel: endScrolling
        }
      : {};

    return (
      <Wrapper>
        {withArrows && !isScrollStart && (
          <ArrowComponent {...arrowControlPropsLeft} />
        )}
        <CarouselContainer
          ref={carouselRef}
          className={className}
          onScroll={updateScrollState}
          _boundariesPadding={boundariesPadding}
          _gap={columnsGap}
          _isDragging={isDraggingState}
          {...dragHandlers}
        >
          {generatedItems.map((item) => item)}
        </CarouselContainer>
        {withArrows && !isScrollEnd && (
          <ArrowComponent {...arrowControlPropsRight} />
        )}
      </Wrapper>
    );
  }
);

export default SnapCarousel;

const Wrapper = styled.div.attrs({
  className: createReadableClass({
    component: 'snap-carousel',
    element: 'wrapper'
  })
})`
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  width: 100%;
`;

const CarouselContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'snap-carousel',
    element: 'container'
  })
})<{
  _gap: number;
  _boundariesPadding: number;
  _isDragging?: boolean;
}>`
  width: 100%;
  position: relative;
  display: flex;
  overflow: scroll hidden;
  scroll-snap-type: x mandatory;
  scroll-snap-stop: always;
  scrollbar-width: none;
  scroll-padding-left: ${({ _boundariesPadding }) => _boundariesPadding}px;
  scroll-padding-right: ${({ _boundariesPadding }) => _boundariesPadding}px;
  padding-right: ${({ _boundariesPadding }) => _boundariesPadding * 2}px;
  gap: ${({ _gap }) => _gap}px;
  user-select: ${({ _isDragging }) => (_isDragging ? 'none' : 'auto')};

  &::-webkit-scrollbar {
    display: none;
  }
`;

function generateData({
  items,
  renderItem
}: {
  items: ReactNode[];
  renderItem: (item: ReactNode, index: number) => JSX.Element;
}) {
  return items.map((_, index) => {
    if (index >= items.length || !items[index]) {
      return <Blank key={index} />;
    }

    return renderItem(items[index], index);
  });
}

const Blank = styled.div``;
